// LiffProvider — initializes the LIFF SDK with the right liffId for the
// current page, fetches the LINE id_token + profile, exchanges them at the
// backend, and stores the session in context.
//
// Children that want the session call useLiff().

import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import liff from "@line/liff";
import type { LiffContextShape, LiffPageKind, LiffSession, LiffProfile } from "./types.js";
import { exchangeLiffSession, LiffExchangeError } from "./exchange.js";

export const LiffContext = createContext<LiffContextShape | null>(null);

interface LiffProviderProps {
  page: LiffPageKind;
  children: React.ReactNode;
  /** If false, render children without blocking on auth (useful for splash). */
  blockUntilReady?: boolean;
}

// Build-time env var names. The CI workflow `deploy-frontend.yml` resolves
// each VITE_LIFF_ID_* by reading the nexus_control.liff_apps table (populated
// by ensure-all-liff.mjs at install-rich-menus time). At dev / preview time
// the fallback table below is used instead.
const LIFF_ID_ENV: Record<LiffPageKind, string> = {
  // legacy 6 + binding
  site_live:     "VITE_LIFF_ID_SITE_LIVE",
  incident:      "VITE_LIFF_ID_INCIDENTS",
  event:         "VITE_LIFF_ID_EVENTS",
  manager_today: "VITE_LIFF_ID_MANAGER_TODAY",
  officer_tasks: "VITE_LIFF_ID_OFFICER_TASKS",
  report:        "VITE_LIFF_ID_REPORTS",
  binding:       "VITE_LIFF_ID_BINDING",
  // patrol family
  patrol_today:               "VITE_LIFF_ID_PATROL_TODAY",
  patrol_live:                "VITE_LIFF_ID_PATROL_LIVE",
  patrol_scan:                "VITE_LIFF_ID_PATROL_SCAN",
  // incident new family (distinct from legacy "incident" picker)
  incident_new:               "VITE_LIFF_ID_INCIDENT_NEW",
  incident_my:                "VITE_LIFF_ID_INCIDENT_MY",
  // reports sub-routes
  reports_site:               "VITE_LIFF_ID_REPORTS_SITE",
  reports_audit:              "VITE_LIFF_ID_REPORTS_AUDIT",
  // schedule / history per role
  manager_schedule:           "VITE_LIFF_ID_MANAGER_SCHEDULE",
  officer_schedule:           "VITE_LIFF_ID_OFFICER_SCHEDULE",
  officer_history:            "VITE_LIFF_ID_OFFICER_HISTORY",
  // auditor
  audit_patrol:               "VITE_LIFF_ID_AUDIT_PATROL",
  audit_log:                  "VITE_LIFF_ID_AUDIT_LOG",
  // tenant_admin
  admin_users:                "VITE_LIFF_ID_ADMIN_USERS",
  admin_invites:              "VITE_LIFF_ID_ADMIN_INVITES",
  // me
  me:                         "VITE_LIFF_ID_ME",
  me_settings:                "VITE_LIFF_ID_ME_SETTINGS",
  // platform (super_admin)
  platform_tenant_switcher:   "VITE_LIFF_ID_PLATFORM_TENANT_SWITCHER",
  platform_announcements:     "VITE_LIFF_ID_PLATFORM_ANNOUNCEMENTS",
  platform_reports:           "VITE_LIFF_ID_PLATFORM_REPORTS",
  platform_health:            "VITE_LIFF_ID_PLATFORM_HEALTH",
  platform_users:             "VITE_LIFF_ID_PLATFORM_USERS",
};

// Placeholder used when a NEW endpoint's VITE_LIFF_ID_* is missing. Falls
// back to the binding LIFF id so the SDK can at least bootstrap and the
// diagnostic panel surfaces the real endpoint mismatch instead of crashing.
const NEW_ENDPOINT_PLACEHOLDER = "2009960133-u3djrvyH";

const LIFF_ID_FALLBACK: Record<LiffPageKind, string> = {
  // legacy 6 + binding (real ids — created long ago on the channel)
  site_live:     "2009960133-bniR9MXk",
  incident:      "2009960133-SiozkbOz",
  event:         "2009960133-iHotJhOg",
  manager_today: "2009960133-4wYj2OBz",
  officer_tasks: "2009960133-3WJCXPqT",
  report:        "2009960133-YDVEkaEn",
  // The binding LIFF id is created by the install-rich-menus workflow
  // (POST /liff/v1/apps via client_credentials grant on the LIFF
  // channel). The literal below is the source of truth for builds
  // where VITE_LIFF_ID_BINDING is not provided. The LIFF Add API in
  // ensure-all-liff.mjs is idempotent (reuses any existing app whose
  // endpoint matches the path in endpoints.json), so this should be a
  // once-per-channel update.
  binding:       "2009960133-u3djrvyH",
  // 21 new endpoints — placeholders. Real ids are wired up at deploy
  // time via VITE_LIFF_ID_* env vars (deploy-frontend.yml reads the
  // nexus_control.liff_apps mapping populated by install-rich-menus).
  patrol_today:               NEW_ENDPOINT_PLACEHOLDER,
  patrol_live:                NEW_ENDPOINT_PLACEHOLDER,
  patrol_scan:                NEW_ENDPOINT_PLACEHOLDER,
  incident_new:               NEW_ENDPOINT_PLACEHOLDER,
  incident_my:                NEW_ENDPOINT_PLACEHOLDER,
  reports_site:               NEW_ENDPOINT_PLACEHOLDER,
  reports_audit:              NEW_ENDPOINT_PLACEHOLDER,
  manager_schedule:           NEW_ENDPOINT_PLACEHOLDER,
  officer_schedule:           NEW_ENDPOINT_PLACEHOLDER,
  officer_history:            NEW_ENDPOINT_PLACEHOLDER,
  audit_patrol:               NEW_ENDPOINT_PLACEHOLDER,
  audit_log:                  NEW_ENDPOINT_PLACEHOLDER,
  admin_users:                NEW_ENDPOINT_PLACEHOLDER,
  admin_invites:              NEW_ENDPOINT_PLACEHOLDER,
  me:                         NEW_ENDPOINT_PLACEHOLDER,
  me_settings:                NEW_ENDPOINT_PLACEHOLDER,
  platform_tenant_switcher:   NEW_ENDPOINT_PLACEHOLDER,
  platform_announcements:     NEW_ENDPOINT_PLACEHOLDER,
  platform_reports:           NEW_ENDPOINT_PLACEHOLDER,
  platform_health:            NEW_ENDPOINT_PLACEHOLDER,
  platform_users:             NEW_ENDPOINT_PLACEHOLDER,
};

function liffIdFor(page: LiffPageKind): string {
  const envKey = LIFF_ID_ENV[page];
  const fromEnv = (import.meta.env as Record<string, string | undefined>)[envKey];
  return fromEnv ?? LIFF_ID_FALLBACK[page];
}

const LIFF_INIT_TIMEOUT_MS = 8000;

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${label}_timeout_${ms}ms`)), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}

function logStep(step: string, data?: unknown): void {
  // eslint-disable-next-line no-console
  console.info(`[liff] ${step}`, data ?? "");
}

// Surfaceable error-detail collection — every step we know how to fail
// adds a structured row, so the splash screen can render the full chain
// when something goes wrong. Critical for iOS in-app browser debugging
// where we have no devtools.
interface DiagRow { step: string; ok: boolean; ms: number; detail?: string }

// Defensive: kill any prior service worker so a stale/broken bundle
// doesn't override our latest deploy. Modern LIFF apps don't need SW.
async function unregisterServiceWorkers(): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const r of regs) {
      try { await r.unregister(); logStep("sw.unregistered", { scope: r.scope }); } catch {}
    }
  } catch {}
}

export function LiffProvider({ page, children, blockUntilReady = true }: LiffProviderProps): React.ReactElement {
  const [isReady, setIsReady] = useState(false);
  const [profile, setProfile] = useState<LiffProfile | null>(null);
  const [session, setSession] = useState<LiffSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [diag, setDiag] = useState<DiagRow[]>([]);
  const initStartedRef = useRef(false);

  const init = useCallback(async () => {
    setError(null);
    setDiag([]);
    const rows: DiagRow[] = [];
    const tick = (label: string, ok: boolean, started: number, detail?: string): void => {
      const row: DiagRow = { step: label, ok, ms: Math.round(performance.now() - started), detail };
      rows.push(row);
      setDiag([...rows]);
      logStep(label, { ok, ms: row.ms, detail });
    };
    const liffId = liffIdFor(page);
    logStep("init.start", { page, liffId, href: window.location.href, ua: navigator.userAgent });

    let t = performance.now();
    try {
      await unregisterServiceWorkers();
      tick("sw.purge", true, t);

      t = performance.now();
      try {
        await withTimeout(liff.init({ liffId, withLoginOnExternalBrowser: true }), LIFF_INIT_TIMEOUT_MS, "liff_init");
        tick("liff.init", true, t, `inClient=${liff.isInClient()} loggedIn=${liff.isLoggedIn()} v=${liff.getVersion?.()} osVer=${liff.getLineVersion?.() ?? "n/a"}`);
      } catch (e) {
        const msg = (e as Error)?.message ?? String(e);
        tick("liff.init", false, t, msg);
        throw e;
      }

      if (!liff.isLoggedIn()) {
        const redirectUri = window.location.href;
        tick("login.redirect", true, t, redirectUri);
        liff.login({ redirectUri });
        return;
      }

      t = performance.now();
      const prof = await liff.getProfile().catch((e) => {
        tick("liff.getProfile", false, t, String(e?.message ?? e));
        throw e;
      });
      tick("liff.getProfile", true, t, `${prof.userId.slice(0, 5)}…`);
      setProfile(prof);

      t = performance.now();
      const idToken = liff.getIDToken();
      tick("liff.getIDToken", !!idToken, t, idToken ? `len=${idToken.length}` : "null");
      if (!idToken) {
        setError(
          "目前無法取得 LINE 登入資訊。請關閉本頁,從 LINE 主選單重新開啟。",
        );
        setIsReady(true);
        return;
      }

      t = performance.now();
      try {
        const sess = await exchangeLiffSession(idToken, page);
        tick("auth.exchange", true, t, `tenant=${sess.tenant_id} role=${sess.role}`);
        setSession(sess);
      } catch (e) {
        if (e instanceof LiffExchangeError) {
          tick("auth.exchange", false, t, `HTTP ${e.status}: ${e.message}`);
        } else {
          tick("auth.exchange", false, t, `${(e as Error)?.name ?? "Error"}: ${(e as Error)?.message ?? String(e)}`);
        }
        throw e;
      }
    } catch (err) {
      const e = err as Error;
      const msg = e?.message ?? String(err);
      logStep("init.fail", { error: msg, name: e?.name, stack: e?.stack?.slice(0, 500) });
      if (err instanceof LiffExchangeError) {
        setError(`身份綁定失敗：${err.message}（HTTP ${err.status}）`);
      } else if (msg.includes("liff_init_timeout")) {
        setError(
          "LIFF SDK 初始化逾時。可能是網路被限制或 LIFF App 設定錯誤。請重新開啟，或回報給管理員。",
        );
      } else if (msg === "Load failed" || msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
        // iOS WebKit fetch failure — most common cause is CORS or DNS.
        setError(`網路請求失敗（${msg}）。請確認可以連線到 api.nexus.sentry.red 並允許 LINE in-app 開啟此網域。`);
      } else {
        setError(`LIFF 初始化失敗：${msg}`);
      }
    } finally {
      setIsReady(true);
    }
  }, [page]);

  useEffect(() => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;
    void init();
  }, [init]);

  useEffect(() => {
    if (!session) return;
    const expMs = new Date(session.expires_at).getTime();
    const delay = Math.max(5_000, expMs - Date.now() - 30_000);
    const t = setTimeout(() => { void init(); }, delay);
    return () => clearTimeout(t);
  }, [session, init]);

  const value = useMemo<LiffContextShape>(() => ({
    isReady,
    isAuthed: !!session,
    error,
    profile,
    session,
    refresh: init,
    logout: () => { setSession(null); try { liff.logout(); } catch {} },
  }), [isReady, profile, session, error, init]);

  if (blockUntilReady && !isReady) {
    return <SplashScreen message="正在連線到 LINE…" diag={diag} />;
  }
  if (blockUntilReady && error && !session) {
    return <SplashScreen message={error} variant="error" onRetry={init} diag={diag} />;
  }

  return <LiffContext.Provider value={value}>{children}</LiffContext.Provider>;
}

interface SplashScreenProps {
  message: string;
  variant?: "loading" | "error";
  onRetry?: () => void;
  diag: DiagRow[];
}

function SplashScreen({ message, variant = "loading", onRetry, diag }: SplashScreenProps): React.ReactElement {
  const buildSha = (import.meta.env.VITE_BUILD_SHA as string | undefined) ?? "dev";
  return (
    <div className="min-h-[100dvh] bg-bg-base text-text-primary flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-2xl bg-bg-surface border border-border p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-2 h-2 rounded-full ${variant === "error" ? "bg-danger" : "bg-accent animate-pulse"}`} aria-hidden />
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            {variant === "error" ? "ERROR" : "INITIALISING"}
          </span>
        </div>
        <p className="text-sm leading-relaxed text-text-secondary whitespace-pre-line">{message}</p>
        {diag.length > 0 && (
          <div className="mt-4 rounded-lg bg-bg-raised border border-border-subtle p-3 max-h-72 overflow-auto">
            <div className="text-[10px] font-mono uppercase tracking-wider text-text-muted mb-2">DIAGNOSTICS</div>
            <ul className="space-y-1">
              {diag.map((d, i) => (
                <li key={i} className="text-[11px] font-mono leading-tight">
                  <span className={d.ok ? "text-success" : "text-danger"}>{d.ok ? "✓" : "✗"}</span>{" "}
                  <span className="text-text-secondary">{d.step}</span>{" "}
                  <span className="text-text-muted">+{d.ms}ms</span>
                  {d.detail && (
                    <div className="pl-4 text-text-muted whitespace-pre-wrap break-all">{d.detail}</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="mt-3 text-[10px] font-mono text-text-muted text-right">
          build: {buildSha} · ua: {(typeof navigator !== "undefined" ? navigator.userAgent : "").slice(0, 40)}…
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-5 w-full h-10 rounded-lg bg-accent text-text-inverse font-medium text-sm hover:bg-accent-hover active:bg-accent-pressed transition-colors"
          >
            重試
          </button>
        )}
      </div>
    </div>
  );
}
